'use client';

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import type { ElementType, MouseEvent } from 'react';
import { Dialog, DialogPortal } from '@ifixit/ui/components/dialog';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { cn } from '@ifixit/ui/lib/utils';
import type {
   AlgoliaHitProps,
   TreeNavigationProps,
   WorkbenchDevice,
   SelectionProps,
   AlgoliaConfig,
} from './types';
import type { LocaleCode } from '@ifixit/i18n/types';
import { addRecentDevice, type RecentDevice, safeLocalStorage } from '@ifixit/utils';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

const NUM_RECENT_DEVICES = 2;
const DevicePicker = lazy(() => import('./DevicePicker'));

export interface DevicePickerModalProps {
   onSelect: (device: AlgoliaHitProps) => void;
   algoliaConfig: AlgoliaConfig;
   oniFixit: boolean;
   langid: string;
   storeLocale: LocaleCode;
   devicesInWorkbench?: WorkbenchDevice[];
   initialDevice?: string;
   selectionProps?: SelectionProps;
   treeNavigationProps?: TreeNavigationProps;
   showUncategorizedDevices?: boolean;
   titleInstruction?: string;
   rootDevice?: string;
   ifixitOrigin?: string;
   wikiTextComponent?: ElementType;
   ignoreAncestors?: string[];
   analyticsKey: string;
   fullScreen?: boolean;
   isOpen?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
}

export const DevicePickerModal = ({
   onSelect,
   algoliaConfig,
   oniFixit,
   langid,
   storeLocale,
   devicesInWorkbench,
   initialDevice,
   selectionProps,
   treeNavigationProps,
   showUncategorizedDevices,
   titleInstruction,
   rootDevice,
   ifixitOrigin,
   wikiTextComponent,
   analyticsKey,
   ignoreAncestors = [],
   fullScreen = false,
   isOpen: controlledIsOpen,
   defaultOpen = false,
   onOpenChange,
}: DevicePickerModalProps) => {
   const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
   const isOpen = controlledIsOpen === undefined ? uncontrolledIsOpen : controlledIsOpen;
   const recentDevices = getRecentDevices();
   const initialQuery =
      typeof window === 'undefined'
         ? ''
         : new URLSearchParams(window.location.search).get('query') || '';

   const onClose = useCallback(() => {
      if (controlledIsOpen === undefined) {
         setUncontrolledIsOpen(false);
      }
      onOpenChange?.(false);
   }, [controlledIsOpen, onOpenChange]);

   const onSubmit = (device: AlgoliaHitProps) => {
      if (device.wikiid) {
         addRecentDevice(device);
      }
      onSelect(device);
   };

   const trackedClose = useCallback(() => {
      trackAnalyticsCustomEvent({
         eventCategory: analyticsKey,
         eventAction: analyticsKey + ' - Picker - Cancel',
         eventName: '',
      });
      onClose();
   }, [analyticsKey, onClose]);

   useEffect(() => {
      if (isOpen) {
         trackAnalyticsCustomEvent({
            eventCategory: analyticsKey,
            eventAction: analyticsKey + ' - Picker - Open',
            eventName: '',
         });
      }
   }, [isOpen, analyticsKey]);

   // Handle Escape key to close (modal={false} disables Radix's built-in handler)
   useEffect(() => {
      if (!isOpen) return;
      const handleEscapeKey = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            trackedClose();
         }
      };
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
   }, [isOpen, trackedClose]);

   // Lock body scroll when modal is open (modal={false} disables Radix's built-in scroll lock)
   useEffect(() => {
      if (!isOpen) return;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
         document.body.style.overflow = originalOverflow;
      };
   }, [isOpen]);

   // Handle click on the backdrop area to close
   const handleBackdropClick = useCallback(
      (event: MouseEvent) => {
         // Only close if the click was on the wrapper itself, not on children
         if (event.target === event.currentTarget) {
            trackedClose();
         }
      },
      [trackedClose]
   );

   return (
      <Dialog open={isOpen} onOpenChange={open => !open && trackedClose()} modal={false}>
         <DialogPortal>
            <div
               aria-hidden="true"
               className={cn(
                  'fixed inset-0 z-[9999]',
                  fullScreen ? 'bg-transparent' : 'bg-black/50'
               )}
               onClick={trackedClose}
            />
            <div
               data-testid="devicePickerComponent"
               className="fixed inset-x-0 bottom-0 top-16 z-[10000] flex items-end justify-center sm:top-0 sm:items-center"
               onClick={handleBackdropClick}
            >
               <div
                  role="dialog"
                  aria-modal="true"
                  className={cn(
                     'relative flex flex-col overflow-hidden bg-white shadow-lg max-h-[800px]',
                     fullScreen
                        ? 'h-screen w-screen max-h-full rounded-none'
                        : 'rounded-t-lg h-[90vh] sm:h-[85vh] w-full sm:w-fit sm:rounded-lg'
                  )}
               >
                  <Suspense fallback={<Skeleton className="h-full w-full" />}>
                     <DevicePicker
                        algoliaConfig={algoliaConfig}
                        onSubmit={onSubmit}
                        onCancel={onClose}
                        oniFixit={oniFixit}
                        langid={langid}
                        devicesInWorkbench={devicesInWorkbench}
                        initialDevice={initialDevice}
                        selectionProps={selectionProps}
                        treeNavigationProps={treeNavigationProps}
                        showUncategorizedDevices={showUncategorizedDevices}
                        titleInstruction={titleInstruction}
                        storeLocale={storeLocale}
                        recentDevices={recentDevices}
                        rootDevice={rootDevice}
                        ifixitOrigin={ifixitOrigin}
                        wikiTextComponent={wikiTextComponent}
                        analyticsKey={analyticsKey}
                        ignoreAncestors={ignoreAncestors}
                        initialQuery={initialQuery}
                        fullScreen={fullScreen}
                     />
                  </Suspense>
               </div>
            </div>
         </DialogPortal>
      </Dialog>
   );
};

export function getRecentDevices(limit: number = NUM_RECENT_DEVICES): RecentDevice[] {
   return safeLocalStorage.getJson<RecentDevice[]>('recentDevices')?.slice(0, limit) ?? [];
}
